import { create } from "zustand";
import type {
  PlaceResult,
  POI,
  Tag,
  CategoryTag,
  ChangelogEvent,
  ChangelogAction,
  EntityType,
} from "@poi-planner/shared";
import { api } from "../services/api";
import { useUserStore } from "./userStore";
import { useAuthStore } from "./authStore";
import { isOfflineMode } from "./connectivityStore";
import { AUTO_SAVE_DELAY_MS } from "@poi-planner/shared";
import { isAccommodationPoi } from "../utils/accommodationSearch";

export type SaveStatus = "saved" | "saving" | "error" | "unsaved";

/**
 * A new library POI for a Google Places result picked outside the POI editor
 * (e.g. a note's From/To), with the same defaults the editor saves.
 */
export function poiFromPlace(place: PlaceResult): Omit<POI, "id"> {
  return {
    name: place.name,
    lat: place.lat,
    lng: place.lng,
    googleMapsUrl: place.googleMapsUrl || undefined,
    notes: null,
    timeBudget: 60,
    tags: [],
    primaryTagId: null,
    availability: null,
    idealDays: null,
    idealTimeOfDay: null,
    weatherRequirement: null,
    sources: [],
    private: false,
  };
}

// A POI with no ownerId (or one that matches the current user) is the
// caller's own; anything else was made visible by a shared trip and must be
// treated as read-only on the client — the server enforces the same rule.
export function isOwnPoi(poi: Pick<POI, "ownerId">): boolean {
  return !poi.ownerId || poi.ownerId === useAuthStore.getState().userId;
}

// Same rule as isOwnPoi, for tags made visible via a shared POI's tags[].
export function isOwnTag(tag: Pick<Tag, "ownerId">): boolean {
  return !tag.ownerId || tag.ownerId === useAuthStore.getState().userId;
}

export function isOwnCategoryTag(
  category: Pick<CategoryTag, "ownerId">,
): boolean {
  return (
    !category.ownerId || category.ownerId === useAuthStore.getState().userId
  );
}

interface LibraryState {
  // Data
  pois: POI[];
  tags: Tag[];
  categoryTags: CategoryTag[];

  // UI state
  loading: boolean;
  error: string | null;
  saveStatus: SaveStatus;

  // Changelog (for library-level changes)
  pendingChangelog: ChangelogEvent[];

  // Actions
  loadLibrary: () => Promise<void>;
  setLibraryData: (
    pois: POI[],
    tags?: Tag[],
    categoryTags?: CategoryTag[],
  ) => void;

  // POI CRUD
  createPOI: (poi: Omit<POI, "id">) => string;
  updatePOI: (id: string, updates: Partial<POI>) => void;
  deletePOI: (id: string) => void;
  getPOI: (id: string) => POI | undefined;

  // Tag CRUD
  createTag: (tag: Omit<Tag, "id">) => Promise<string>;
  updateTag: (id: string, updates: Partial<Omit<Tag, "id">>) => Promise<void>;
  deleteTag: (id: string) => Promise<void>;
  getTag: (id: string) => Tag | undefined;

  createCategoryTag: (name: string) => Promise<string>;
  updateCategoryTag: (id: string, name: string) => Promise<void>;
  deleteCategoryTag: (id: string) => Promise<void>;
  setCategoryTagMembers: (id: string, tagIds: string[]) => Promise<void>;
  setTagCategories: (tagId: string, categoryTagIds: string[]) => Promise<void>;
  reorderCategoryTags: (categoryTagIds: string[]) => Promise<void>;

  // Accommodation helpers (POIs with "accommodation" tag)
  getAccommodationPOIs: () => POI[];
  getAccommodation: (id: string) => POI | undefined;
}

// Auto-save debounce state
let saveTimeout: ReturnType<typeof setTimeout> | null = null;
let pendingFlush: Promise<void> | null = null;

async function doSave(): Promise<void> {
  // Offline edits stay queued; resumeLibrarySave sends them on reconnect.
  if (isOfflineMode()) {
    useLibraryStore.setState({ saveStatus: "unsaved" });
    return;
  }
  const { pois, pendingChangelog } = useLibraryStore.getState();

  useLibraryStore.setState({ saveStatus: "saving" });
  try {
    // Save only the caller's own POIs — foreign POIs (visible via a shared
    // trip) must never round-trip through the full-library replace.
    await api.savePOIs(pois.filter(isOwnPoi));

    // Save changelog if there are pending entries
    if (pendingChangelog.length > 0) {
      await api.saveLibraryChangelog(pendingChangelog);
      useLibraryStore.setState({ pendingChangelog: [] });
    }

    useLibraryStore.setState({ saveStatus: "saved" });
  } catch {
    useLibraryStore.setState({
      saveStatus: isOfflineMode() ? "unsaved" : "error",
    });
  }
}

/** Send library edits that were queued while the connection was down. */
export async function resumeLibrarySave(): Promise<void> {
  const { saveStatus } = useLibraryStore.getState();
  if (saveStatus === "saved" || saveStatus === "saving" || saveTimeout) return;
  pendingFlush = doSave().finally(() => {
    pendingFlush = null;
  });
  await pendingFlush;
}

function queueSave() {
  if (saveTimeout) clearTimeout(saveTimeout);
  useLibraryStore.setState({ saveStatus: "unsaved" });

  saveTimeout = setTimeout(() => {
    saveTimeout = null;
    pendingFlush = doSave().finally(() => {
      pendingFlush = null;
    });
  }, AUTO_SAVE_DELAY_MS);
}

// Changelog helper
function addChangelog(
  action: ChangelogAction,
  entityType: EntityType,
  entityId: string,
  summary: string,
  before: Record<string, unknown> | null,
  after: Record<string, unknown> | null,
) {
  const user = useUserStore.getState().displayName || "Unknown";
  const userId = useAuthStore.getState().user?.id ?? null;
  const event: ChangelogEvent = {
    id: crypto.randomUUID(),
    timestamp: new Date().toISOString(),
    user,
    userId,
    action,
    entityType,
    entityId,
    summary,
    before,
    after,
    undoOf: null,
  };
  const pending = useLibraryStore.getState().pendingChangelog;
  useLibraryStore.setState({ pendingChangelog: [...pending, event] });
}

export const useLibraryStore = create<LibraryState>((set, get) => ({
  pois: [],
  tags: [],
  categoryTags: [],
  loading: true,
  error: null,
  saveStatus: "saved" as SaveStatus,
  pendingChangelog: [],

  loadLibrary: async () => {
    set({ loading: true, error: null });
    try {
      const data = await api.loadLibrary();
      set({
        pois: data.pois,
        tags: data.tags ?? [],
        categoryTags: data.categoryTags ?? [],
        loading: false,
      });
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
    }
  },

  setLibraryData: (pois, tags, categoryTags) => {
    set({
      pois,
      ...(tags !== undefined ? { tags } : {}),
      ...(categoryTags !== undefined ? { categoryTags } : {}),
    });
  },

  // --- POI CRUD ---

  createPOI: (poiData: Omit<POI, "id">) => {
    if (isOfflineMode()) {
      throw new Error("You're offline. Editing is turned off.");
    }
    const id = crypto.randomUUID();
    const ownerId = useAuthStore.getState().userId ?? undefined;
    const poi: POI = {
      ...poiData,
      id,
      ownerId,
      private: poiData.private ?? false,
      tags: [
        ...new Set([
          ...poiData.tags,
          ...(poiData.primaryTagId ? [poiData.primaryTagId] : []),
        ]),
      ],
    };
    const { pois } = get();
    set({ pois: [...pois, poi] });
    addChangelog("create", "poi", id, `Created "${poi.name}"`, null, {
      name: poi.name,
    });
    queueSave();
    return id;
  },

  updatePOI: (id: string, updates: Partial<POI>) => {
    if (isOfflineMode()) return;
    const { pois } = get();
    const idx = pois.findIndex((p) => p.id === id);
    if (idx === -1) return;
    const before = pois[idx];
    if (!isOwnPoi(before)) return;
    const updated = { ...before, ...updates, id };
    updated.tags = [
      ...new Set([
        ...updated.tags,
        ...(updated.primaryTagId ? [updated.primaryTagId] : []),
      ]),
    ];
    const newPois = [...pois];
    newPois[idx] = updated;
    set({ pois: newPois });
    addChangelog(
      "update",
      "poi",
      id,
      `Updated "${updated.name}"`,
      { name: before.name },
      { name: updated.name },
    );
    queueSave();
  },

  deletePOI: (id: string) => {
    if (isOfflineMode()) return;
    const { pois } = get();
    const poi = pois.find((p) => p.id === id);
    if (!poi) return;
    if (!isOwnPoi(poi)) return;
    const newPois = pois.filter((p) => p.id !== id);
    set({ pois: newPois });
    addChangelog(
      "delete",
      "poi",
      id,
      `Deleted "${poi.name}"`,
      { name: poi.name },
      null,
    );
    queueSave();
  },

  getPOI: (id: string) => {
    return get().pois.find((p) => p.id === id);
  },

  // --- Tag CRUD ---

  createTag: async (tagData: Omit<Tag, "id">) => {
    const tag = await api.createTag(tagData);
    const { tags } = get();
    set({ tags: [...tags, tag] });
    return tag.id;
  },

  updateTag: async (id: string, updates: Partial<Omit<Tag, "id">>) => {
    const updatedTag = await api.updateTag(id, updates);
    const { tags } = get();
    const idx = tags.findIndex((t) => t.id === id);
    if (idx !== -1) {
      const newTags = [...tags];
      newTags[idx] = updatedTag;
      set({ tags: newTags });
    }
  },

  deleteTag: async (id: string) => {
    await api.deleteTag(id);
    const { tags, pois, categoryTags } = get();
    // Remove tag from list
    const newTags = tags.filter((t) => t.id !== id);
    // Remove canonical membership and clear the marker reference.
    const newPois = pois.map((p) =>
      p.primaryTagId === id || p.tags.includes(id)
        ? {
            ...p,
            primaryTagId: p.primaryTagId === id ? null : p.primaryTagId,
            tags: p.tags.filter((tagId) => tagId !== id),
          }
        : p,
    );
    set({
      tags: newTags,
      pois: newPois,
      categoryTags: categoryTags.map((category) => ({
        ...category,
        tagIds: category.tagIds.filter((tagId) => tagId !== id),
      })),
    });
    // Save POIs if any were updated
    if (pois.some((p) => p.tags.includes(id) || p.primaryTagId === id)) {
      queueSave();
    }
  },

  getTag: (id: string) => {
    return get().tags.find((t) => t.id === id);
  },

  createCategoryTag: async (name) => {
    const category = await api.createCategoryTag(name);
    set({ categoryTags: [...get().categoryTags, category] });
    addChangelog(
      "create",
      "category_tag",
      category.id,
      `Created category "${category.name}"`,
      null,
      { name: category.name },
    );
    queueSave();
    return category.id;
  },

  updateCategoryTag: async (id, name) => {
    const before = get().categoryTags.find((category) => category.id === id);
    const updated = await api.updateCategoryTag(id, name);
    set({
      categoryTags: get().categoryTags.map((category) =>
        category.id === id ? updated : category,
      ),
    });
    addChangelog(
      "update",
      "category_tag",
      id,
      `Updated category "${updated.name}"`,
      before ? { name: before.name } : null,
      { name: updated.name },
    );
    queueSave();
  },

  deleteCategoryTag: async (id) => {
    const category = get().categoryTags.find((item) => item.id === id);
    await api.deleteCategoryTag(id);
    set({
      categoryTags: get().categoryTags.filter((item) => item.id !== id),
    });
    if (category) {
      addChangelog(
        "delete",
        "category_tag",
        id,
        `Deleted category "${category.name}"`,
        { name: category.name },
        null,
      );
      queueSave();
    }
  },

  setCategoryTagMembers: async (id, tagIds) => {
    const before = get().categoryTags.find((category) => category.id === id);
    const updated = await api.setCategoryTagMembers(id, [...new Set(tagIds)]);
    set({
      categoryTags: get().categoryTags.map((category) =>
        category.id === id ? updated : category,
      ),
    });
    addChangelog(
      "update",
      "category_tag",
      id,
      `Updated members of category "${updated.name}"`,
      before ? { tagIds: before.tagIds } : null,
      { tagIds: updated.tagIds },
    );
    queueSave();
  },

  setTagCategories: async (tagId, categoryTagIds) => {
    const beforeIds = get()
      .categoryTags.filter(
        (category) =>
          isOwnCategoryTag(category) && category.tagIds.includes(tagId),
      )
      .map((category) => category.id);
    await api.setTagCategories(tagId, [...new Set(categoryTagIds)]);
    const selected = new Set(categoryTagIds);
    set({
      categoryTags: get().categoryTags.map((category) => {
        if (!isOwnCategoryTag(category)) return category;
        const tagIds = new Set(category.tagIds);
        if (selected.has(category.id)) tagIds.add(tagId);
        else tagIds.delete(tagId);
        return { ...category, tagIds: [...tagIds] };
      }),
    });
    addChangelog(
      "update",
      "tag",
      tagId,
      "Updated category membership for a tag",
      { categoryTagIds: beforeIds },
      { categoryTagIds },
    );
    queueSave();
  },

  reorderCategoryTags: async (categoryTagIds) => {
    const beforeIds = get()
      .categoryTags.filter(isOwnCategoryTag)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((category) => category.id);
    await api.reorderCategoryTags(categoryTagIds);
    const order = new Map(categoryTagIds.map((id, index) => [id, index]));
    set({
      categoryTags: get().categoryTags.map((category) =>
        order.has(category.id)
          ? { ...category, sortOrder: order.get(category.id)! }
          : category,
      ),
    });
    if (categoryTagIds[0]) {
      addChangelog(
        "update",
        "category_tag",
        categoryTagIds[0],
        "Reordered category tags",
        { categoryTagIds: beforeIds },
        { categoryTagIds },
      );
      queueSave();
    }
  },

  // --- Accommodation helpers ---
  // Accommodations are now POIs with the "accommodation" tag

  getAccommodationPOIs: () => {
    const { pois, tags } = get();
    return pois.filter((poi) => isAccommodationPoi(poi, tags));
  },

  getAccommodation: (id: string) => {
    const { pois, tags } = get();
    const poi = pois.find((p) => p.id === id);
    if (!poi) return undefined;
    return isAccommodationPoi(poi, tags) ? poi : undefined;
  },
}));

// Export flush function for external use (e.g., before navigation)
export async function flushLibrarySave(): Promise<void> {
  if (saveTimeout) {
    clearTimeout(saveTimeout);
    saveTimeout = null;
    await doSave();
  } else if (pendingFlush) {
    await pendingFlush;
  }
}
